// =============================================================================
//  Purpose   : templating decode helpers (no saga side effects)
// =============================================================================
import { get, has, size, keys, values, isNil, isString } from 'ldash';
import log from 'uix/logging';
import { decodeUrl, decodeDomain, decodeRequest } from 'uix/routing';
import { UnloadedDomainError } from 'uix/error';
const decodeGoParams = (newRequest, oldRequest, templatingcfgs)=>{
    /* go with structured request params */ if (has(newRequest, 'tab')) throw Error('use "tabname" instead of "tab"');
    if (has(newRequest, 'class')) throw Error('use "classname" instead of "class"');
    if (isNil(newRequest) || size(newRequest) == 0) {
        // reload current view
        var updateQuery = {};
        var request = {};
    } else {
        // fetch new request path params and seperate query params
        var { query, updateQuery, keepQuery, ...request } = newRequest;
        if (!updateQuery && keepQuery) {
            updateQuery = {};
        }
    }
    // use merged updateQuery into oldQuery for new query string
    if (updateQuery) {
        if (has(updateQuery, 'id')) {
            // special case a updateQuery with id, move to request params
            request.id = updateQuery.id;
            delete updateQuery.id;
        }
        // use old + given values instead of given 'query'
        query = {
            ...oldRequest.query,
            ...updateQuery
        };
        // delete query params with value "null"
        query = keys(query).reduce((r, e)=>{
            if (query[e] !== null) r[e] = query[e];
            return r;
        }, {});
    }
    // if no new dname given, reuse old
    let dname = request.dname || oldRequest.dname;
    let templatingcfg = templatingcfgs[dname];
    if (!templatingcfg?.classes) {
        throw new UnloadedDomainError(dname);
    }
    let isDefault = {
        tabname: false,
        classname: false
    };
    const classname = request.classname || oldRequest.classname;
    // choose default tab for class if no tab/ctxid/id param is given
    // only classname case: determine default tab
    if (!request.id && !request.ctxid && !request.tabname && request.classname) {
        const tabs = values(templatingcfg.classes[classname].tabs);
        let tabname = get(tabs.filter((t)=>t['default']), '0.name');
        if (!tabname) {
            log.error('route generation failed: got no default tab for classname %o', classname);
            return {} // XXX: throw?
            ;
        }
        request = {
            ...request,
            tabname
        };
        isDefault.tabname = true;
    }
    return {
        templatingcfg,
        request,
        query,
        isDefault
    };
};
const decodeGoUrl = (url, domains, templatingcfgs, deploy)=>{
    /* go with url string */ let { params, query } = decodeUrl(url);
    let dname = decodeDomain(params, domains, deploy.pathPrefix);
    let templatingcfg = templatingcfgs[dname];
    if (!templatingcfg) {
        throw new UnloadedDomainError(dname);
    }
    let { query: typedQuery, authNeeded, isDefault, ...request } = decodeRequest(params, query, {
        domains: domains,
        templating: {
            [dname]: templatingcfg
        },
        deploy
    });
    return {
        templatingcfg,
        request,
        query: typedQuery,
        isDefault,
        dname
    };
};
const customizeRouting = (request, query, oldRequest, templatingcfgs)=>{
    /* apply some custom route changing rules
    we are using the rules of the target domain here, but for the current (old) classname
    returns either a modified request params and/or modified query params
  */ let merge = (prev, next)=>({
            ...prev,
            ...next || {}
        });
    let templatingcfg = templatingcfgs[request.dname];
    let tcfg_class = templatingcfg.classes[oldRequest.classname];
    let customizeRoutingClass = tcfg_class?.customizeRouting;
    let customizeRoutingTab = tcfg_class?.tabs[oldRequest.tabname]?.customizeRouting;
    // first apply tablevel rules
    let customized = customizeRoutingTab ? customizeRoutingTab(request, query, oldRequest) : {};
    request = merge(request, customized?.request);
    query = merge(query, customized?.query);
    // then apply classlevel rules
    if (customizeRoutingClass) {
        customized = customizeRoutingClass ? customizeRoutingClass(request, query, oldRequest) : {};
        request = merge(request, customized?.request);
        query = merge(query, customized?.query);
    }
    return {
        request,
        query
    };
};
export const decodeGo = (urlOrRequest, oldRequest, domains, templatingcfgs, deploy)=>{
    if (isString(urlOrRequest)) {
        var { request, query, templatingcfg, isDefault, dname } = decodeGoUrl(urlOrRequest, domains, templatingcfgs, deploy);
    } else {
        let { odid } = urlOrRequest;
        if (odid !== undefined && domains.odid2name[odid]) {
            urlOrRequest = {
                ...urlOrRequest,
                dname: domains.odid2name[odid]
            };
        }
        var { request, query, templatingcfg, isDefault, dname } = decodeGoParams(urlOrRequest, oldRequest, templatingcfgs);
    }
    if (!dname) {
        dname = request.dname || oldRequest.dname;
    }
    // pop query from old request
    let { query: oldQuery, ...filtered } = oldRequest;
    // overwrite old request params with incoming
    let merged = {
        ...filtered,
        ...request
    };
    let customized = customizeRouting(merged, query, oldRequest, templatingcfgs);
    merged = customized.request;
    query = customized.query;
    // give them names
    let { cname, classname, tabname, ctxid, id } = merged;
    let namedParams = {
        dname,
        cname,
        classname,
        tabname,
        ctxid,
        id
    };
    let pathParams = [
        domains.name2prefix[dname],
        cname
    ];
    if (!isNil(ctxid) || !isNil(id)) {
        pathParams = [
            ...pathParams,
            classname,
            tabname,
            ctxid,
            id
        ];
    } else if (!isDefault.classname) {
        // build reduced version (optional class/tabname)
        pathParams = [
            ...pathParams,
            classname
        ];
        if (!isDefault.tabname) {
            pathParams = [
                ...pathParams,
                tabname,
                ctxid,
                id
            ];
        }
    }
    pathParams = pathParams.filter((e)=>e !== '' && e !== null && e !== undefined);
    // bring them in order, filter and join them
    let pathname = '/' + pathParams.join('/');
    return {
        pathname,
        pathParams,
        namedParams,
        query,
        templatingcfg
    };
};
